import {
  ADMIN_GROUP_MANAGEMENT_MENU_ITEM,
  ADMIN_SIGNALS_MENU_ITEM,
  ADMIN_TRADING_CONFIGURATION_MENU_ITEM,
  AFFILIATION_MENU_ITEM,
  DASHBOARD_MENU_ITEM,
  getBotsMenuItems,
  MARKETPLACE_MENU_ITEM,
  TRADES_MENU_ITEM,
} from "common/Sider/Sider";
import Text from "common/Typography/Text";
import styled, { useTheme } from "styled-components";
import useRoutesMatch from "utils/hooks/useRoutesMatch";
import { useHistory } from "react-router-dom";
import { HEADER_ICON_SIZE } from "@shared/common/Themes/constants";
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import PieChartOutlined from "@ant-design/icons/PieChartOutlined";
import React from "react";
import { DashboardIcon } from "common/Icons/Icons";
import {
  BOTS_PATH,
  PORTFOLIO_ACCOUNTS_PATH,
  PORTFOLIO_PATH,
} from "common/constants";
import { MenuItemConfig } from "types";
import Dropdown from "common/Dropdown/Dropdown";
import { selectWorkspace } from "@shared/slices/common.slice";
import { useSelector } from "react-redux";
import { WorkspaceType } from "@shared/types";
import { useBottomNavigator, useVirtualKeyboard } from "utils/hooks/misc";

interface BottomNavigatorProps {
  setMenuOpen: (open: boolean) => void;
}

const BottomNavigator = ({ setMenuOpen }: BottomNavigatorProps) => {
  const theme = useTheme();
  const history = useHistory();
  const workspace = useSelector(selectWorkspace);
  const menuItems =
    workspace === WorkspaceType.ADMIN ? ADMIN_MENU_ITEMS : USER_MENU_ITEMS;
  const bottomNavigatorData = useBottomNavigator();
  const { isKeyboardOpen } = useVirtualKeyboard();

  const [selectedIndex] =
    useRoutesMatch(
      menuItems.map((item) => item.routerMatch),
      false
    ) || [];

  const textStyles = (active: boolean) => {
    return {
      fontSize: 10,
      height: 15,
      color: theme.colors[active ? "primaryBlue" : "secondaryGrey"],
      paddingTop: 4,
    };
  };

  const botsMenuItems = getBotsMenuItems(true, theme.isDarkMode).map((item) => {
    return {
      key: item.key,
      label: item.name,
      onClick: () => history.push(item.link),
      icon: item.icon,
    };
  });

  const getBotsMenuItem = (item: MenuItemConfig, index: number) => {
    const selected = index === selectedIndex;
    return (
      <Dropdown
        title="Bot Type"
        menu={{ items: botsMenuItems }}
        trigger={["click"]}
        placement="top"
        key={item.key}
      >
        <ItemWrapper>
          <IconWrapper $selected={selected}>{item.icon}</IconWrapper>
          <Text style={textStyles(selected)}>{item.name}</Text>
        </ItemWrapper>
      </Dropdown>
    );
  };

  const getMenuItem = (item: MenuItemConfig, index: number) => {
    const selected = index === selectedIndex;
    return (
      <ItemWrapper
        key={item.key}
        onClick={() => item.link && history.push(item.link)}
      >
        <IconWrapper $selected={selected}>{item.icon}</IconWrapper>
        <Text style={textStyles(selected)}>{item.name}</Text>
      </ItemWrapper>
    );
  };

  return (
    <Wrapper $height={bottomNavigatorData.height} $hide={isKeyboardOpen}>
      {menuItems.map((item, index) => {
        if (item.key === "bots") {
          return getBotsMenuItem(item, index);
        } else {
          return getMenuItem(item, index);
        }
      })}
      <ItemWrapper onClick={() => setMenuOpen(true)}>
        <IconWrapper>
          <MenuOutlined
            style={{
              fontSize: HEADER_ICON_SIZE,
              marginTop: "auto",
              marginBottom: "auto",
            }}
          />
        </IconWrapper>
        <Text style={textStyles(false)}>Menu</Text>
      </ItemWrapper>
    </Wrapper>
  );
};

export default BottomNavigator;

const BOTS_MENU_ITEM = {
  key: "bots",
  name: "Bots",
  icon: <DashboardIcon />,
  link: BOTS_PATH,
  routerMatch: BOTS_PATH,
};
const PORTFOLIO_MENU_ITEM = {
  key: "portfolio",
  name: "Portfolio",
  icon: <PieChartOutlined />,
  link: PORTFOLIO_ACCOUNTS_PATH,
  routerMatch: PORTFOLIO_PATH,
};
const USER_MENU_ITEMS = [
  DASHBOARD_MENU_ITEM,
  PORTFOLIO_MENU_ITEM,
  MARKETPLACE_MENU_ITEM,
  BOTS_MENU_ITEM,
  TRADES_MENU_ITEM,
];
const ADMIN_MENU_ITEMS = [
  { ...ADMIN_GROUP_MANAGEMENT_MENU_ITEM, name: "Users" },
  { ...ADMIN_TRADING_CONFIGURATION_MENU_ITEM, name: "Configuration" },
  ADMIN_SIGNALS_MENU_ITEM,
  AFFILIATION_MENU_ITEM,
];

const Wrapper = styled.div<{ $height: number; $hide?: boolean }>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  padding-top: 8px;
  background-color: ${(p) => p.theme.colors.layoutBgColor};
  border-top: 1px solid ${(p) => p.theme.colors.lightestBorderGrey};
  height: ${(p) => p.$height}px;
  z-index: 1000;
  ${(p) =>
    p.$hide &&
    `
      display: none;
    `};
  ${(p) =>
    !p.$height &&
    `
    padding-top: 0;
  `};
`;
const ItemWrapper = styled.div`
  flex: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const IconWrapper = styled.div<{ $selected?: boolean }>`
  width: 22px;
  height: 22px;
  color: ${(p) =>
    p.theme.colors[p.$selected ? "primaryBlue" : "secondaryGrey"]};
  svg {
    color: ${(p) =>
      p.theme.colors[p.$selected ? "primaryBlue" : "secondaryGrey"]};
    width: 22px;
    height: 22px;
  }
`;
